import { useRef, useState } from 'react';

import { useForm } from '@toolbox/form';
import { useWidgetContext } from './WidgetContext';
import { extractZipCodeFromString } from '@widget/core/utils/extractZipCodeFromString';
import { zipCodesApi } from '@widget/api/zipCodesApi';
import { getSearchParam } from '@widget/core/utils/getSearchParam';

import type { AnalyticsMap } from '@widget/utils/getAnalyticsFields';
import type { LAYOUT_HEADER_TYPE } from '@widget/storyblok/contants';
import type {
  FieldValues,
  UseFormReturn,
  DefaultValues,
  UnpackNestedValue,
  FieldErrors,
} from '@toolbox/form';
import type { Modify } from '@toolbox/utils';
import type { StepState } from './types';
import type { Richtext } from 'storyblok-js-client';

type SubmitSuccess<FD> = { data: UnpackNestedValue<FD> };
type SubmitFailure<FD extends FieldValues> = { errors: FieldErrors<FD> };
type SubmitResponse<FD extends FieldValues> = SubmitSuccess<FD> | SubmitFailure<FD>;

export type StepProps<F extends Record<string, unknown> = Record<string, unknown>> = {
  id: string;
  /**
   * when true, the user cannot navigate back to the previous steps
   */
  noReturn?: boolean;
  /**
   * when true, hides live valuation
   */
  hideLiveValuation?: boolean | null;

  /**
   * when true, hides live USP
   */
  hideUSP?: boolean | null;

  /**
   * when true, disables localized usp and shows standard usp
   */
  disableLocalizedUsp?: boolean | null;

  /**
   * when true, disables Autofocus on first element
   */
  disableAutofocus?: boolean | null;

  /**
   * when true, hides footer
   **/
  hideFooter?: boolean | null;
  /**
   * when true, hides footer
   **/
  socialProofMessage?: Richtext | null;
  /**
   * function to lazily determine if the step should be rendered depending on runtime data
   */
  renderWhen?: (data: F) => boolean;
  /**
   * id of the next step. On completion of the current step, the widget would attempt to navigate to
   * the step with this id
   */
  nextStep?: string;
  /**
   * Display USP text vertically or horizontally
   */
  uspLayout?: 'vertical' | 'horizontal' | null;
  /**
   * Switch on/off header text
   */
  hideHeaderText?: boolean;
  /**
   * Swap usp text and trust icons
   */
  swapUspTextAndTrustIcons?: boolean;
  /**
   * Full width layout
   */
  fullWidthLayout?: boolean;
  /**
   * Map to send step data to google analytics: { [formDataFieldName]: analyticsPayloadFieldName }
   */
  analyticsMap?: AnalyticsMap;

  children?: {
    props: Record<string, any>;
  };

  headerType?: (typeof LAYOUT_HEADER_TYPE)[keyof typeof LAYOUT_HEADER_TYPE] | null;
  name?: string;
  /**
   * Define which Regional trends to show
   */
  showUSPList?: 'oldRegionalTrends' | 'newRegionalTrends' | 'none' | null | string;

  /**
   * Flag is used to determine if the step is the final step in the flow and show the progress bar accordingly.
   */
  finalStep?: boolean;

  /**
   * Flag is used to determine if the progress bar should be visible.
   */
  showProgressBar?: boolean;
};

type UseStepMethods<FD extends FieldValues = FieldValues> = {
  setNextStep: (nextStepId: string) => void;
  submit: (data: FD, nextStepId?: string) => void | Promise<void>;
  skip: (nextStepId?: string) => Promise<void> | void;
  stepData: StepState<FD> | undefined;
};

export type UseStepFormMethods<FD extends FieldValues = FieldValues> = Modify<
  UseStepMethods<FD>,
  {
    submit: (nextStepId?: string) => Promise<SubmitResponse<FD>>;
  }
>;

export type UseStepFormReturn<FD extends FieldValues = FieldValues> = UseStepFormMethods<FD> & {
  formMethods: UseFormReturn<FD>;
  fetching: boolean;
};

const fetchFormDataMapping = {
  isCoveredByPartners: async (formData: Record<string, unknown>) => {
    if (formData.zipCode) {
      const zipCodeExtracted = extractZipCodeFromString(formData.zipCode as string);
      return await zipCodesApi(zipCodeExtracted);
    }
  },
};

type fetchFormDataMappingKey = keyof typeof fetchFormDataMapping;
export const useStepForm = <FD extends FieldValues = FieldValues>({
  id,
  nextStep,
}: StepProps): UseStepFormReturn<FD> => {
  const widget = useWidgetContext();
  const stepData = widget.getStep<FD>(id);
  const nextStepRef = useRef(nextStep);
  const formMethods = useForm<FD>({
    mode: 'onTouched',
    defaultValues: {
      ...(stepData?.formData as DefaultValues<FD>),
      tp_channel: getSearchParam('tp_channel') || 'false',
      hasGetambassadorShortCode: getSearchParam('getambassador_short_code') ? 'true' : 'false',
    },
  });
  const [fetching, setFetching] = useState(false);

  function skip(nextStepId?: string) {
    return widget.skip(id, nextStepId ?? nextStepRef.current);
  }

  function setNextStep(nextStep: string) {
    nextStepRef.current = nextStep;
  }

  function submit(nextStepId?: string) {
    return new Promise<SubmitResponse<FD>>((resolve) => {
      formMethods.handleSubmit(
        async (data) => {
          let additionalFields = {};
          for (const dataKey in data) {
            if (
              !data[dataKey] &&
              typeof fetchFormDataMapping[dataKey as fetchFormDataMappingKey] === 'function'
            ) {
              setFetching(true);
              additionalFields =
                (await fetchFormDataMapping[dataKey as fetchFormDataMappingKey](data)) || {};
              setFetching(false);
            }
          }
          widget.commit(id, { ...data, ...additionalFields }, nextStepId ?? nextStepRef.current);
          resolve({ data: { ...data, ...additionalFields } });
        },
        (errors) => resolve({ errors }),
      )();
    });
  }

  return {
    stepData,
    skip,
    setNextStep,
    formMethods,
    submit,
    fetching,
  };
};
